import { useCallback, useEffect, useState } from 'react';
import { useStyletron } from 'baseui';
import { NotificationCircle } from 'baseui/badge';
import { DatePicker } from 'baseui/datepicker';
import { Drawer } from 'baseui/drawer';
import { FormControl } from 'baseui/form-control';
import { Heading, HeadingLevel } from 'baseui/heading';
import { Input, SIZE } from 'baseui/input';
import { StyledLink } from 'baseui/link';
import { ALIGN, Radio, RadioGroup } from 'baseui/radio';
import { Select } from 'baseui/select';
import { TableBuilder, TableBuilderColumn } from 'baseui/table-semantic';
import { toaster } from 'baseui/toast';
import { faFilter } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { DateTime } from 'luxon';
import { getQualityTrackerProjects, listQAers, saveQualityMetadata, updateProjectDetailV5 } from '@gtintel/platform-api';
import { Button, ButtonRow } from '@gtintel/platform-atoms';
import { useAsyncOp } from '@gtintel/platform-utils';
const DATE_FORMAT = {
  weekday: 'short',
  day: 'numeric',
  month: 'short'
};
const columnStyles = {
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis'
};
const QA_STAGES = [{
  id: 'np_assigned',
  label: 'NP Assigned'
}, {
  id: 'np_accepted',
  label: 'NP Accepted'
}, {
  id: 'report_received',
  label: 'Report Received'
}, {
  id: 'pending_review',
  label: 'Pending Review'
}, {
  id: 'qa_in_progress',
  label: 'QA in Progress'
}, {
  id: 'reviewed',
  label: 'Reviewed'
}, {
  id: 'np_delay',
  label: 'NP Delay'
}, {
  id: 'qa_delay',
  label: 'QA Delay'
}, {
  id: 'rework',
  label: 'Sent for Rework'
}, {
  id: 'completed',
  label: 'Completed'
}];
const DateRangeFieldNames = [{
  value: 'due',
  label: 'Client Due'
}, {
  value: 'np_due',
  label: 'NP Due'
}];
const dateObjectToDateString = date => {
  return DateTime.fromJSDate(date).toISODate();
};
function QATrackerTable() {
  const [css, theme] = useStyletron();
  const [rows, setRows] = useState([]);
  const [qaChoices, setQaChoices] = useState([]);
  const [editedRow, setEditedRow] = useState(null);

  // Filter specific
  const [isOpen, setIsOpen] = useState(false);
  const [selectedQAers, setSelectedQAers] = useState([]);
  const [selectableQAers, setSelectableQAers] = useState([]);
  const [isFilterLoading, setIsFilterLoading] = useState(false);
  const [dateRangeValue, setDateRangeValue] = useState([]);
  const [dateRangeFieldName, setDateRangeFieldName] = useState('');
  const [term, setTerm] = useState('');
  const [selectedStages, setSelectedStages] = useState([]);
  const [appliedFiltersCount, setAppliedFiltersCount] = useState(0);
  const [params, setParams] = useState({
    name: null,
    qa_assigned: [],
    stages: []
  });
  const {
    isLoading,
    fn: getQaTrackerProjects
  } = useAsyncOp(getQualityTrackerProjects);
  const {
    fn: getQaChoices
  } = useAsyncOp(listQAers);
  const {
    fn: updateQAMetadata,
    isLoading: isSavingMetadata
  } = useAsyncOp(saveQualityMetadata);
  const {
    fn: updateProject
  } = useAsyncOp(updateProjectDetailV5);
  const clearFilterParams = useCallback(() => {
    setSelectedQAers([]);
    setSelectedStages([]);
    setDateRangeValue([]);
    setAppliedFiltersCount(0);
    setTerm('');
    setParams({
      name: null,
      qa_assigned: []
    });
  }, [setTerm, setSelectedQAers, setSelectedStages]);
  const setFilterParams = useCallback(() => {
    let count = 0;
    const param = {
      name: null,
      qa_assigned: [],
      stages: []
    };
    for (const qa of selectedQAers) {
      param.qa_assigned.push(qa.id);
      count += 1;
    }
    for (const stage of selectedStages) {
      param.stages.push(stage.id);
      count += 1;
    }
    if (term !== null) {
      param.name = term;
      count += 1;
    }
    if (dateRangeFieldName && dateRangeValue && dateRangeValue.length > 0) {
      param[dateRangeFieldName] = dateRangeValue;
      count += 1;
    }
    setParams(param);
    setAppliedFiltersCount(count);
  }, [selectedQAers, term, dateRangeFieldName, dateRangeValue, selectedStages]);
  useEffect(() => {
    getQaChoices().then(data => {
      setQaChoices(data.results);
      setSelectableQAers(data.results.map(user => {
        return {
          id: String(user.id),
          label: user.full_name
        };
      }));
    }).catch(error => {
      toaster.negative('Error fetching QA choices', error);
    });
  }, [getQaChoices]);
  useEffect(() => {
    const paramsArray = [];
    if (params.name) {
      paramsArray.push(['name', params.name]);
    }
    if (params.qa_assigned.length > 0) {
      for (const qaer of params.qa_assigned) {
        paramsArray.push([`qa_assigned`, qaer]);
      }
    }
    if (params.stages && params.stages.length > 0) {
      for (const stage of params.stages) {
        paramsArray.push(['stage', stage]);
      }
    }
    if (params.due && params.due.length > 0) {
      paramsArray.push(['requested_completion_date_after', dateObjectToDateString(params.due[0])]);
      paramsArray.push(['requested_completion_date_before', dateObjectToDateString(params.due[1])]);
    }
    if (params.completed && params.completed.length > 0) {
      paramsArray.push(['completed_date_after', dateObjectToDateString(params.completed[0])]);
      paramsArray.push(['completed_date_before', dateObjectToDateString(params.completed[1])]);
    }
    if (params.np_due && params.np_due.length > 0) {
      paramsArray.push(['np_completion_date_after', dateObjectToDateString(params.np_due[0])]);
      paramsArray.push(['np_completion_date_before', dateObjectToDateString(params.np_due[1])]);
    }
    const searchParams = new URLSearchParams(paramsArray);
    getQaTrackerProjects(searchParams.toString()).then(data => {
      setRows(data);
    }).catch(error => {
      toaster.negative('Error fetching QA Tracker', error);
    });
  }, [getQaTrackerProjects, params]);
  const handleNotesChange = useCallback((row, notesUpdate) => {
    setEditedRow({
      ...row,
      quality_metadata: {
        ...row.quality_metadata,
        notes: notesUpdate
      }
    });
  }, []);
  const handleSetQAer = useCallback((projectId, userId) => {
    updateProject(String(projectId), {
      qa_assigned: userId
    }).then(() => {
      setRows(prev => prev.map(r => r.id === projectId ? {
        ...r,
        qa_assigned: userId,
        quality_metadata: {
          ...r.quality_metadata
        }
      } : r));
      toaster.positive('Saved Metadata');
      setEditedRow(null);
    }).catch(error => {
      toaster.negative('Error saving metadata', error);
      setEditedRow(null);
    });
  }, [updateProject]);
  const handleSave = useCallback((projectId, metadataUpdate) => {
    console.info('metadataUpdate', metadataUpdate);
    updateQAMetadata(projectId, {
      ...metadataUpdate,
      second_qa: metadataUpdate.second_qa ? metadataUpdate.second_qa.id : metadataUpdate.second_qa
    }).then(() => {
      setRows(prev => prev.map(r => r.id === projectId ? {
        ...r,
        quality_metadata: {
          ...r.quality_metadata,
          ...metadataUpdate
        }
      } : r));
      toaster.positive('Saved Metadata');
      setEditedRow(null);
    }).catch(error => {
      toaster.negative('Error saving metadata', error);
      setEditedRow(null);
    });
  }, [updateQAMetadata]);
  return <div className={css({
    width: '100%',
    WebkitOverflowScrolling: 'touch',
    '::-webkit-scrollbar': {
      height: '8px'
    },
    '::-webkit-scrollbar-track': {
      background: theme.colors.backgroundTertiary
    },
    '::-webkit-scrollbar-thumb': {
      background: theme.colors.borderOpaque,
      borderRadius: '4px'
    }
  })}>
      <div className={css({
      alignItems: 'center',
      display: 'flex',
      flexFlow: 'row no wrap',
      justifyContent: 'space-evenly',
      gap: '8px',
      marginBottom: '24px'
    })}>
        <div className={css({
        display: 'flex',
        flex: '1 0 0'
      })}>
          <Input value={term} onKeyPress={e => {
          if (e.key === 'Enter') {
            setFilterParams();
          }
        }} onChange={e => {
          setTerm(e.target.value);
          setIsFilterLoading(true);
          setTimeout(() => {
            setIsFilterLoading(false);
          }, 2000);
        }} placeholder="Search for project name, client name, QAer name or email..." overrides={{
          Root: {
            style: {
              width: '100%'
            }
          },
          Input: {
            props: {
              'data-testid': 'text-input'
            }
          }
        }} />
        </div>

        <NotificationCircle content={<span>{appliedFiltersCount}</span>}>
          <Button disabled={isFilterLoading} kind="secondary" testId="show-more-filters" onClick={() => {
          setIsOpen(!isOpen);
        }}>
            <FontAwesomeIcon icon={faFilter} size="lg" />
          </Button>
        </NotificationCircle>
      </div>

      <TableBuilder data={rows} emptyMessage="No projects due for QA" isLoading={isLoading} loadingMessage="Loading...">
        <TableBuilderColumn header="Project" key="project">
          {row => <div className={css({
          display: 'flex',
          justifyContent: 'space-between'
        })}>
              <span className={css({
            marginRight: '4px'
          })}>{row.id}</span>
              <StyledLink href={`/admin/project/${row.id}/overview`} target="_blank">
                {row.name}
              </StyledLink>
            </div>}
        </TableBuilderColumn>

        <TableBuilderColumn header="Client" key="client">
          {row => <div className={css(columnStyles)}>{row.client}</div>}
        </TableBuilderColumn>
        <TableBuilderColumn header="Dates" key="dates">
          {row => <div className={css({
          display: 'flex',
          flexDirection: 'column',
          minWidth: '200px'
        })}>
              {[{
            name: 'Client Due',
            date: row.requested_completion_date
          }, {
            name: 'NP Due',
            date: row.np_completion_date
          }].map(keyDate => <div key={keyDate.date}>
                  <span className={css({
              fontWeight: 'bold'
            })}>
                    {keyDate.name}
                  </span>
                  <span>
                    {keyDate.date ? `: ${DateTime.fromISO(keyDate.date).toLocaleString(DATE_FORMAT)}` : ''}
                  </span>
                </div>)}
            </div>}
        </TableBuilderColumn>
        <TableBuilderColumn header="1st QA" key="qa_1">
          {row => {
          const user = qaChoices.filter(user => row.qa_assigned ? user.id === row.qa_assigned : null);
          return <div className={css(columnStyles)}>
                <Select size={SIZE.mini} isLoading={isLoading} disabled={isSavingMetadata} options={qaChoices.map(user => ({
              id: user.id,
              label: `${user.first_name} ${user.last_name}`
            }))} value={user && user.length > 0 ? [{
              id: user[0].id,
              label: user[0].full_name
            }] : null} onChange={params => {
              const proceed = window.confirm(`You are about to set the QAer to ${String(params.value.length > 0 ? params.value[0].label : 'Empty')}. Is this correct?`);
              if (proceed) {
                if (params.value && params.value.length > 0) {
                  handleSetQAer(row.id, +params.value[0].id);
                } else {
                  handleSetQAer(row.id, null);
                }
              }
            }} clearable />
              </div>;
        }}
        </TableBuilderColumn>
        <TableBuilderColumn key="qa_2" header="2nd QA">
          {row => {
          return <div className={css(columnStyles)}>
                <Select size={SIZE.mini} clearable={true} isLoading={isLoading} disabled={isSavingMetadata} options={qaChoices.map(user => ({
              id: user.id,
              label: `${user.first_name} ${user.last_name}`
            }))} value={row.quality_metadata?.second_qa ? [{
              id: row.quality_metadata.second_qa.id,
              label: `${row.quality_metadata.second_qa.first_name} ${row.quality_metadata.second_qa.last_name}`
            }] : null} onChange={params => {
              if (params.value && params.value.length > 0) {
                handleSave(row.id, {
                  second_qa: params.value[0]
                });
              } else {
                handleSave(row.id, {
                  second_qa: null
                });
              }
            }} />
              </div>;
        }}
        </TableBuilderColumn>
        <TableBuilderColumn key="stage" header="Stage">
          {row => {
          const currentStage = row.quality_metadata?.stage;
          return <div className={css(columnStyles)}>
                <Select size={SIZE.mini} clearable={false} isLoading={isLoading} disabled={isSavingMetadata} value={currentStage ? [QA_STAGES.find(stage => stage.id === currentStage) || {
              id: currentStage,
              label: currentStage
            }] : []} onChange={params => {
              if (params.value && params.value.length > 0) {
                handleSave(row.id, {
                  stage: params.value[0].id.toString()
                });
              }
            }} options={QA_STAGES} />
              </div>;
        }}
        </TableBuilderColumn>

        <TableBuilderColumn key="notes" header="Notes">
          {row => {
          const isEditing = editedRow?.id === row.id;
          const currentValue = isEditing ? editedRow.quality_metadata?.notes || '' : row.quality_metadata?.notes || '';
          return <div className={css(columnStyles)}>
                <Input size={SIZE.mini} disabled={isSavingMetadata} value={currentValue} onChange={e => {
              handleNotesChange(row, e.currentTarget.value);
            }} onBlur={() => {
              if (isEditing) {
                handleSave(row.id, {
                  notes: editedRow.quality_metadata?.notes
                });
              }
            }} />
              </div>;
        }}
        </TableBuilderColumn>
      </TableBuilder>

      <Drawer isOpen={isOpen} autoFocus onClose={() => setIsOpen(false)} overrides={{
      Root: {
        props: {
          'data-testid': 'drawer'
        }
      },
      DrawerContainer: {
        style: {
          paddingTop: '64px'
        }
      },
      Close: {
        style: {
          top: '77px'
        }
      }
    }}>
        <div className={css({
        display: 'flex',
        flexFlow: 'column nowrap',
        gap: '8px',
        marginBottom: '14px'
      })}>
          <HeadingLevel>
            <Heading>Filters</Heading>
          </HeadingLevel>

          <div>
            <FormControl label={() => 'Date to filter on'}>
              <RadioGroup value={dateRangeFieldName} onChange={e => setDateRangeFieldName(e.currentTarget.value)} name="Date field to filter on" align={ALIGN.horizontal}>
                {DateRangeFieldNames.map(field => {
                return <Radio value={field.value}>{field.label}</Radio>;
              })}
              </RadioGroup>
            </FormControl>
          </div>

          <div>
            <FormControl label={() => 'Date range filter'}>
              <DatePicker placeholder="Date between.." value={dateRangeValue} onChange={({
              date
            }) => {
              setDateRangeValue(Array.isArray(date) ? date : [date]);
            }} range clearable quickSelect overrides={{
              Input: {
                props: {
                  'data-testid': 'date-picker'
                }
              }
            }} />
            </FormControl>
          </div>

          <div>
            <FormControl label={() => 'Stage'}>
              <Select value={selectedStages} disabled={isFilterLoading} multi={true} placeholder="Select one or more stages..." onChange={params => {
              setSelectedStages(params.value);
            }} options={QA_STAGES} overrides={{
              Input: {
                props: {
                  'data-testid': 'stages-select'
                }
              }
            }} />
            </FormControl>
          </div>

          <div>
            <FormControl label={() => 'Selected QAer'}>
              <Select value={selectedQAers} disabled={isFilterLoading} multi={true} placeholder="Select a QAer..." onChange={params => {
              setSelectedQAers(params.value);
            }} options={selectableQAers} overrides={{
              Input: {
                props: {
                  'data-testid': 'qaers-select'
                }
              }
            }} />
            </FormControl>
          </div>

          <ButtonRow>
            <Button testId="apply-filters" onClick={() => {
            setIsOpen(false);
            setIsFilterLoading(true);
            setFilterParams();
            setTimeout(() => {
              setIsFilterLoading(false);
            }, 500);
          }}>
              Apply Filters
            </Button>
            <Button testId="clear-filters" onClick={() => {
            setIsFilterLoading(true);
            setIsOpen(false);
            clearFilterParams();
            setTimeout(() => {
              setIsFilterLoading(false);
            }, 2000);
          }} kind="error">
              Clear Filters
            </Button>
          </ButtonRow>
        </div>
      </Drawer>
    </div>;
}
export default QATrackerTable;